import { Clapperboard, ExternalLink, Star, Sparkles } from "lucide-react";

const recommendedTools = [
  {
    name: "Google Veo",
    bestFor: "Realistic videos",
    points: ["Creates realistic scenes from text or images", "Can generate sound and dialogue", "Good for cinematic advertising"],
    url: "https://labs.google/flow",
  },
  {
    name: "Runway",
    bestFor: "Professional videos",
    points: ["Text-to-video and image-to-video", "Advanced editing and camera movement", "Good for business advertisements"],
    url: "https://runwayml.com",
  },
  {
    name: "Kling AI",
    bestFor: "Realistic movement",
    points: ["Turns pictures into moving videos", "Creates people, cars, buildings and landscapes", "Good for storytelling"],
    url: "https://klingai.com",
  },
  {
    name: "HeyGen",
    bestFor: "Talking people",
    points: ["Creates an AI presenter", "Makes videos from written scripts", "Useful for explaining your business"],
    url: "https://heygen.com",
  },
  {
    name: "InVideo AI",
    bestFor: "Beginners",
    points: ["Write one idea and generate a video", "AI creates the script, voice and scenes", "Good for YouTube and advertisements"],
    url: "https://invideo.io",
  },
  {
    name: "Pika",
    bestFor: "Short creative videos",
    points: ["Special effects and animation", "Useful for TikTok and Instagram"],
    url: "https://pika.art",
  },
  {
    name: "CapCut",
    bestFor: "Editing your videos",
    points: ["Adds subtitles and music", "Cuts and joins video clips", "Adds transitions and effects"],
    url: "https://capcut.com",
  },
];

const moreTools = [
  {
    name: "OpenAI Sora",
    bestFor: "Cinematic realism",
    points: ["Very realistic text-to-video scenes", "Strong for product and story visuals"],
    url: "https://sora.com",
  },
  {
    name: "Luma Dream Machine",
    bestFor: "Fast realistic clips",
    points: ["Text or image to video in minutes", "Natural motion, free starter credits"],
    url: "https://lumalabs.ai/dream-machine",
  },
  {
    name: "Synthesia",
    bestFor: "Business presenters",
    points: ["AI avatars speak 120+ languages", "Good for training and explainer videos"],
    url: "https://synthesia.io",
  },
  {
    name: "Hailuo AI",
    bestFor: "Realistic motion",
    points: ["Popular for lifelike people and movement", "Free daily generation credits"],
    url: "https://hailuoai.video",
  },
  {
    name: "Pictory",
    bestFor: "Script to video",
    points: ["Turns written text into videos with stock footage", "Automatic captions and voiceover"],
    url: "https://pictory.ai",
  },
  {
    name: "Fliki",
    bestFor: "Voice-driven videos",
    points: ["Text to video with realistic AI voices", "Good for ads and social posts"],
    url: "https://fliki.ai",
  },
  {
    name: "Descript",
    bestFor: "Editing by text",
    points: ["Edit video by editing the transcript", "Remove filler words with one click"],
    url: "https://descript.com",
  },
  {
    name: "Veed.io",
    bestFor: "Quick online editing",
    points: ["Subtitles, translation and templates in the browser", "Good for social media formats"],
    url: "https://veed.io",
  },
  {
    name: "Opus Clip",
    bestFor: "Short clips from long videos",
    points: ["Turns long videos into short vertical clips", "Finds the most engaging moments automatically"],
    url: "https://www.opus.pro",
  },
];

const ToolCard = ({ tool, groupKey }) => (
  <div
    className="bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/50 p-5 transition-all duration-300 flex flex-col"
    data-testid={`${groupKey}-card-${tool.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
  >
    <div className="flex items-start justify-between gap-3 mb-3">
      <Clapperboard className="w-6 h-6 text-[#D4AF37] flex-shrink-0" strokeWidth={1.5} />
      <span className="text-[9px] uppercase tracking-wider px-2 py-1 rounded-sm bg-[#D4AF37]/15 text-[#D4AF37] text-right">
        Best for {tool.bestFor}
      </span>
    </div>
    <h3 className="font-semibold text-white text-base mb-2">{tool.name}</h3>
    <ul className="space-y-1.5 mb-4 flex-1">
      {tool.points.map((point) => (
        <li key={point} className="text-xs text-muted-foreground flex items-start gap-2">
          <span className="mt-1.5 w-1 h-1 rounded-full bg-[#D4AF37] flex-shrink-0" />
          {point}
        </li>
      ))}
    </ul>
    <a
      href={tool.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black text-xs uppercase tracking-widest py-2.5 transition-all duration-300"
      data-testid={`${groupKey}-visit-${tool.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
    >
      Visit Website <ExternalLink className="w-3.5 h-3.5" />
    </a>
  </div>
);

const AiVideoPage = () => {
  return (
    <div className="pt-20 min-h-screen" data-testid="ai-video-page">
      {/* Header */}
      <section className="py-16 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="text-sm uppercase tracking-widest text-[#D4AF37]">AI Tools</span>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mt-4 font-serif">
            AI Video Creator Tools
          </h1>
          <p className="text-muted-foreground mt-4">
            Websites that create videos from text, images and scripts — for your business advertisements, products and social media. Learn them one by one.
          </p>
        </div>
      </section>

      {/* Recommended */}
      <section className="py-12 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-3 mb-6">
            <Star className="w-7 h-7 text-[#D4AF37]" />
            <h2 className="text-2xl md:text-3xl font-semibold font-serif">Recommended — Learn These First</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendedTools.map((tool) => <ToolCard key={tool.name} tool={tool} groupKey="recommended" />)}
          </div>
        </div>
      </section>

      {/* More tools */}
      <section className="py-12 bg-[#0A0A0A]/50">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="w-7 h-7 text-[#D4AF37]" />
            <h2 className="text-2xl md:text-3xl font-semibold font-serif">More AI Video Tools</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {moreTools.map((tool) => <ToolCard key={tool.name} tool={tool} groupKey="more" />)}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AiVideoPage;
